import { useCallback, useEffect, useMemo, useState } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';
import './Templates.css';

/**
 * /templates — meeting-type confirmation + reminder template editor.
 *
 * Mounted at scheduler.annuityassociation.com/templates. **Admin-only**
 * (PR #38). Advisors do not have access to this editor — confirmation
 * copy is centrally managed to keep the brand voice consistent across
 * the roster and reduce risk of off-brand edits to the message a
 * prospect sees at booking. If a non-admin advisor lands here (e.g.
 * stale bookmark), they get a 'forbidden' empty state with a link
 * back to the portal dashboard.
 *
 * Confirmation templates are per-advisor × meeting_type. The brand
 * shell (header, footer, signature, headshot) stays in code; this
 * editor only controls the *message*. Reminder templates are generic
 * per-meeting-type × tier × channel.
 *
 * V1 UI: textarea-based editor with a click-to-insert variable
 * chip row + a live preview pane that renders sample data through
 * the template-render substitution. No rich-text editor (TipTap
 * etc.) — typical use is plain paragraphs, and bringing in a rich
 * editor would 5x the bundle size for marginal benefit.
 */

// Mirrors the variable schema in api/_lib/template-render.ts.
const TEMPLATE_VARIABLES: ReadonlyArray<{
  name: string;
  label: string;
  example: string;
}> = [
  { name: 'prospectFirstName', label: 'Prospect first name',  example: 'Sarah' },
  { name: 'prospectLastName',  label: 'Prospect last name',   example: 'Mitchell' },
  { name: 'advisorFirstName',  label: 'Advisor first name',   example: 'Jeremiah' },
  { name: 'advisorLastName',   label: 'Advisor last name',    example: 'Konger' },
  { name: 'advisorFullName',   label: 'Advisor full name',    example: 'Jeremiah Konger' },
  { name: 'advisorPhone',      label: 'Advisor phone',        example: '(561) 555-0123' },
  { name: 'advisorEmail',      label: 'Advisor email',        example: 'jeremiah@annuityassociation.com' },
  { name: 'meetingDate',       label: 'Meeting date',         example: 'Tuesday, May 5, 2026' },
  { name: 'meetingTime',       label: 'Meeting time',         example: '10:00 AM ET' },
  { name: 'meetingDateTime',   label: 'Meeting date + time',  example: 'Tuesday, May 5, 2026 at 10:00 AM ET' },
  { name: 'meetingTypeName',   label: 'Meeting type name',    example: '2nd Meeting' },
  { name: 'zoomJoinUrl',       label: 'Zoom join URL',        example: 'https://zoom.us/j/123456789' },
  { name: 'rescheduleUrl',     label: 'Reschedule link',      example: 'https://scheduler.annuityassociation.com/r/abc' },
  { name: 'cancelUrl',         label: 'Cancel link',          example: 'https://scheduler.annuityassociation.com/r/abc' },
];

const SAMPLE_VARS_BASE: Record<string, string> = Object.fromEntries(
  TEMPLATE_VARIABLES.map((v) => [v.name, v.example])
);

/**
 * Render with sample data. The currently-selected meeting type's display
 * name overrides the static '2nd Meeting' example so switching tabs in
 * the editor updates the preview live (Zach reported this — preview
 * was stuck on '2nd Meeting' regardless of selected type).
 */
function renderPreview(template: string, meetingTypeName?: string): string {
  const vars = meetingTypeName
    ? { ...SAMPLE_VARS_BASE, meetingTypeName }
    : SAMPLE_VARS_BASE;
  return template.replace(/\{\{(\w+)\}\}/g, (_m, name: string) => vars[name] ?? '');
}

interface MeetingType {
  id: string;
  slug: string;
  displayName: string;
  displayOrder: number;
}

interface ConfirmationTemplate {
  id: string;
  advisorId: string;
  meetingTypeId: string;
  meetingTypeSlug: string;
  meetingTypeName: string;
  emailSubject: string;
  emailBody: string;
  smsBody: string;
  closingLine: string;
  updatedAt: string;
}

interface ReminderTemplate {
  id: string;
  meetingTypeId: string;
  meetingTypeName: string;
  tierLabel: string;
  channel: string;
  subject: string | null;
  body: string;
  updatedAt: string;
}

interface AdvisorOption {
  id: string;
  fullName: string;
}

const PORTAL_BASE = 'https://aaportal.annuityassociation.com';

export function Templates() {
  const auth = useCurrentUser();

  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      window.location.href = redirectToPortalLogin();
    }
  }, [auth.status]);

  // PR #38 — Admin-only page. Non-admin advisors get a clean 'forbidden'
  // empty state instead of seeing the editor. The /api/templates/*
  // endpoints also require admin server-side, so this is defense in
  // depth (UX line first, then API).
  if (auth.status === 'authenticated' && !auth.user.isAdmin) {
    return (
      <div className="admin-shell">
        <header className="admin-header">
          <Logo className="admin-logo" />
          <div className="admin-header-text">
            <h1>Meeting templates</h1>
            <p className="admin-sub">Admin-only surface</p>
          </div>
        </header>
        <main className="admin-main">
          <section className="admin-card">
            <h2>This editor is admin-only</h2>
            <p className="admin-card-desc">
              Confirmation and reminder copy is centrally managed to keep
              the brand voice consistent across the roster. If you'd like
              your confirmation message tweaked, ping Zach and he'll
              update it from this editor.
            </p>
            <p className="admin-card-desc">
              <a href={`${PORTAL_BASE}/dashboard`}>← Back to the portal dashboard</a>
            </p>
          </section>
        </main>
      </div>
    );
  }

  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [advisors, setAdvisors] = useState<AdvisorOption[]>([]);
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string | null>(null);
  const [selectedMeetingTypeId, setSelectedMeetingTypeId] = useState<string | null>(null);
  const [confirmationTemplates, setConfirmationTemplates] = useState<ConfirmationTemplate[]>([]);
  const [reminderTemplates, setReminderTemplates] = useState<ReminderTemplate[]>([]);
  const [tab, setTab] = useState<'confirmation' | 'reminders'>('confirmation');
  const [errBanner, setErrBanner] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Load meeting types + advisor list (admin) once on mount
  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    let cancelled = false;
    (async () => {
      try {
        const mtRes = await authedFetch('/api/templates/meeting-types');
        const mtJson = await mtRes.json();
        if (!cancelled && mtJson.ok) {
          setMeetingTypes(mtJson.meetingTypes);
          if (mtJson.meetingTypes.length > 0) {
            setSelectedMeetingTypeId(mtJson.meetingTypes[0].id);
          }
        }

        // Build the advisor list. Admins see all active advisors;
        // non-admin advisors are pinned to their own row.
        if (auth.user.isAdmin) {
          const advRes = await authedFetch('/api/admin/advisors');
          const advJson = await advRes.json();
          if (!cancelled && advJson.ok) {
            const list: AdvisorOption[] = (advJson.advisors ?? []).map((a: { id: string; fullName: string; isActive: boolean }) => ({
              id: a.id,
              fullName: a.fullName,
            }));
            list.sort((a, b) => a.fullName.localeCompare(b.fullName));
            setAdvisors(list);
            // Default selection: if the admin is also an advisor, pick themselves; otherwise the first.
            const initialId = auth.user.advisorId && list.some((a) => a.id === auth.user.advisorId)
              ? auth.user.advisorId
              : list[0]?.id ?? null;
            setSelectedAdvisorId(initialId);
          }
        } else if (auth.user.advisorId) {
          setAdvisors([{ id: auth.user.advisorId, fullName: auth.user.email }]);
          setSelectedAdvisorId(auth.user.advisorId);
        }
      } catch (e) {
        if (!cancelled) setErrBanner(e instanceof Error ? e.message : 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  // Load confirmation templates whenever the selected advisor changes
  useEffect(() => {
    if (!selectedAdvisorId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch(`/api/templates/advisor?advisor_id=${encodeURIComponent(selectedAdvisorId)}`);
        const json = await res.json();
        if (!cancelled && json.ok) setConfirmationTemplates(json.templates);
        else if (!cancelled) setErrBanner(json.message ?? 'Failed to load templates');
      } catch (e) {
        if (!cancelled) setErrBanner(e instanceof Error ? e.message : 'Network error');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedAdvisorId]);

  // Load reminder templates (admin only)
  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.user.isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/templates/reminders');
        const json = await res.json();
        if (!cancelled && json.ok) setReminderTemplates(json.templates);
      } catch (e) {
        if (!cancelled) console.error('Failed to load reminder templates', e);
      }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  const currentConfirmation = useMemo(
    () => confirmationTemplates.find((t) => t.meetingTypeId === selectedMeetingTypeId) ?? null,
    [confirmationTemplates, selectedMeetingTypeId]
  );

  const updateConfirmation = useCallback(
    (patch: Partial<ConfirmationTemplate>) => {
      if (!currentConfirmation) return;
      setConfirmationTemplates((prev) =>
        prev.map((t) => (t.id === currentConfirmation.id ? { ...t, ...patch } : t))
      );
    },
    [currentConfirmation]
  );

  async function saveConfirmation() {
    if (!currentConfirmation || !selectedAdvisorId) return;
    setSaving(true);
    setErrBanner(null);
    try {
      const res = await authedFetch('/api/templates/advisor', {
        method: 'PUT',
        body: JSON.stringify({
          advisorId: selectedAdvisorId,
          meetingTypeId: currentConfirmation.meetingTypeId,
          emailSubject: currentConfirmation.emailSubject,
          emailBody: currentConfirmation.emailBody,
          smsBody: currentConfirmation.smsBody,
          closingLine: currentConfirmation.closingLine,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setErrBanner(json.message ?? `HTTP ${res.status}`);
      } else {
        setSavedAt(new Date());
      }
    } catch (e) {
      setErrBanner(e instanceof Error ? e.message : 'Network error');
    } finally {
      setSaving(false);
    }
  }

  async function saveReminder(t: ReminderTemplate) {
    setSaving(true);
    setErrBanner(null);
    try {
      const res = await authedFetch('/api/templates/reminders', {
        method: 'PUT',
        body: JSON.stringify({ id: t.id, subject: t.subject, body: t.body }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) setErrBanner(json.message ?? `HTTP ${res.status}`);
      else setSavedAt(new Date());
    } catch (e) {
      setErrBanner(e instanceof Error ? e.message : 'Network error');
    } finally {
      setSaving(false);
    }
  }

  const updateReminder = useCallback((id: string, patch: Partial<ReminderTemplate>) => {
    setReminderTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  if (auth.status === 'loading') {
    return <p className="admin-empty">Loading…</p>;
  }
  if (auth.status === 'unauthenticated') return null;

  const currentAdvisorName =
    advisors.find((a) => a.id === selectedAdvisorId)?.fullName ?? '(unknown)';

  return (
    <div className="admin-app">
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1 className="admin-title">Meeting Templates</h1>
            <p className="admin-sub">
              Edit confirmation messages per meeting type
              {auth.user.isAdmin && ', plus generic reminder copy (admin only)'}
              .
              {savedAt && (
                <>
                  {' · saved '}
                  <strong>{savedAt.toLocaleTimeString()}</strong>
                </>
              )}
            </p>
          </div>
        </div>
        <nav className="admin-nav">
          <button
            type="button"
            className={`admin-nav-link ${tab === 'confirmation' ? 'admin-nav-link-active' : ''}`}
            onClick={() => setTab('confirmation')}
          >
            Confirmation messages
          </button>
          {auth.user.isAdmin && (
            <button
              type="button"
              className={`admin-nav-link ${tab === 'reminders' ? 'admin-nav-link-active' : ''}`}
              onClick={() => setTab('reminders')}
            >
              Reminder copy (admin)
            </button>
          )}
        </nav>
      </header>

      <main className="admin-main">
        {errBanner && (
          <div className="admin-err" role="alert">
            <strong>Error:</strong> {errBanner}{' '}
            <button type="button" className="link-btn" onClick={() => setErrBanner(null)}>
              dismiss
            </button>
          </div>
        )}

        {loading && <p className="admin-empty">Loading templates…</p>}

        {!loading && tab === 'confirmation' && (
          <ConfirmationEditor
            advisors={advisors}
            isAdmin={auth.user.isAdmin}
            selectedAdvisorId={selectedAdvisorId}
            setSelectedAdvisorId={setSelectedAdvisorId}
            currentAdvisorName={currentAdvisorName}
            meetingTypes={meetingTypes}
            selectedMeetingTypeId={selectedMeetingTypeId}
            setSelectedMeetingTypeId={setSelectedMeetingTypeId}
            template={currentConfirmation}
            onChange={updateConfirmation}
            onSave={saveConfirmation}
            saving={saving}
          />
        )}

        {!loading && tab === 'reminders' && auth.user.isAdmin && (
          <ReminderEditor
            templates={reminderTemplates}
            onChange={updateReminder}
            onSave={saveReminder}
            saving={saving}
          />
        )}
      </main>
    </div>
  );
}

interface ConfirmationEditorProps {
  advisors: AdvisorOption[];
  isAdmin: boolean;
  selectedAdvisorId: string | null;
  setSelectedAdvisorId: (id: string) => void;
  currentAdvisorName: string;
  meetingTypes: MeetingType[];
  selectedMeetingTypeId: string | null;
  setSelectedMeetingTypeId: (id: string) => void;
  template: ConfirmationTemplate | null;
  onChange: (patch: Partial<ConfirmationTemplate>) => void;
  onSave: () => void;
  saving: boolean;
}

function ConfirmationEditor({
  advisors, isAdmin, selectedAdvisorId, setSelectedAdvisorId, currentAdvisorName,
  meetingTypes, selectedMeetingTypeId, setSelectedMeetingTypeId, template,
  onChange, onSave, saving,
}: ConfirmationEditorProps) {
  // Currently-selected meeting type's display name flows into the
  // preview so {{meetingTypeName}} reflects the active tab.
  const currentMeetingTypeName =
    meetingTypes.find((mt) => mt.id === selectedMeetingTypeId)?.displayName;
  return (
    <div className="templates-layout">
      <aside className="templates-sidebar">
        {isAdmin && advisors.length > 1 && (
          <div className="templates-field">
            <label htmlFor="advisor-select">Advisor</label>
            <select
              id="advisor-select"
              value={selectedAdvisorId ?? ''}
              onChange={(e) => setSelectedAdvisorId(e.target.value)}
            >
              {advisors.map((a) => (
                <option key={a.id} value={a.id}>{a.fullName}</option>
              ))}
            </select>
          </div>
        )}
        {!isAdmin && (
          <div className="templates-field templates-field-readonly">
            <label>Editing templates for</label>
            <strong>{currentAdvisorName}</strong>
          </div>
        )}
        <div className="templates-field">
          <label htmlFor="meeting-type-select">Meeting type</label>
          <ul className="templates-mt-list">
            {meetingTypes.map((mt) => (
              <li key={mt.id}>
                <button
                  type="button"
                  className={`templates-mt-btn ${mt.id === selectedMeetingTypeId ? 'templates-mt-btn-active' : ''}`}
                  onClick={() => setSelectedMeetingTypeId(mt.id)}
                >
                  {mt.displayName}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <section className="templates-editor">
        {!template && (
          <p className="admin-empty">No template found for this advisor + meeting type.</p>
        )}
        {template && (
          <>
            <div className="templates-vars">
              <strong>Available variables (click to copy):</strong>
              <div className="templates-vars-grid">
                {TEMPLATE_VARIABLES.map((v) => (
                  <button
                    key={v.name}
                    type="button"
                    className="templates-var-chip"
                    title={v.label + ' — example: ' + v.example}
                    onClick={() => navigator.clipboard.writeText(`{{${v.name}}}`)}
                  >
                    {`{{${v.name}}}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="templates-field">
              <label>Email subject</label>
              <input
                type="text"
                value={template.emailSubject}
                onChange={(e) => onChange({ emailSubject: e.target.value })}
                maxLength={300}
              />
              <div className="templates-preview">
                Preview: <em>{renderPreview(template.emailSubject, currentMeetingTypeName)}</em>
              </div>
            </div>

            <div className="templates-field">
              <label>Email body</label>
              <textarea
                rows={12}
                value={template.emailBody}
                onChange={(e) => onChange({ emailBody: e.target.value })}
                maxLength={20_000}
              />
              <details className="templates-preview-details">
                <summary>Preview rendered with sample data</summary>
                <pre className="templates-preview-pre">{renderPreview(template.emailBody, currentMeetingTypeName)}</pre>
              </details>
            </div>

            <div className="templates-field">
              <label>Closing line</label>
              <input
                type="text"
                value={template.closingLine}
                onChange={(e) => onChange({ closingLine: e.target.value })}
                maxLength={200}
              />
              <div className="templates-preview">
                Preview: <em>{renderPreview(template.closingLine, currentMeetingTypeName)}</em>
              </div>
            </div>

            <div className="templates-field">
              <label>SMS body</label>
              <textarea
                rows={3}
                value={template.smsBody}
                onChange={(e) => onChange({ smsBody: e.target.value })}
                maxLength={1600}
              />
              <div className="templates-preview">
                {template.smsBody.length} / 1600 chars · Preview:{' '}
                <em>{renderPreview(template.smsBody, currentMeetingTypeName)}</em>
              </div>
            </div>

            <div className="templates-actions">
              <button type="button" className="advisor-toggle advisor-toggle-on" disabled={saving} onClick={onSave}>
                {saving ? 'Saving…' : 'Save template'}
              </button>
              <span className="templates-help">
                Brand shell, header/footer, and advisor signature are added automatically.
                Only the message content above is editable here.
              </span>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

interface ReminderEditorProps {
  templates: ReminderTemplate[];
  onChange: (id: string, patch: Partial<ReminderTemplate>) => void;
  onSave: (t: ReminderTemplate) => void;
  saving: boolean;
}

function ReminderEditor({ templates, onChange, onSave, saving }: ReminderEditorProps) {
  // Group by meeting type for readability.
  const grouped = useMemo(() => {
    const m = new Map<string, ReminderTemplate[]>();
    for (const t of templates) {
      const list = m.get(t.meetingTypeName) ?? [];
      list.push(t);
      m.set(t.meetingTypeName, list);
    }
    return [...m.entries()];
  }, [templates]);

  return (
    <div className="reminder-editor">
      <p className="admin-section-help">
        Reminder copy is shared across all advisors for consistency. Edits here affect every advisor's reminders for the selected (meeting type, tier, channel) combination.
      </p>
      {grouped.map(([typeName, list]) => (
        <section key={typeName} className="reminder-type-group">
          <h2 className="admin-section-title">{typeName}</h2>
          {list.map((t) => (
            <div key={t.id} className="reminder-row">
              <div className="reminder-row-meta">
                <strong>{tierLabelDisplay(t.tierLabel)}</strong> · {t.channel.toUpperCase()}
              </div>
              {t.channel === 'email' && (
                <div className="templates-field">
                  <label>Subject</label>
                  <input
                    type="text"
                    value={t.subject ?? ''}
                    onChange={(e) => onChange(t.id, { subject: e.target.value })}
                    maxLength={300}
                  />
                </div>
              )}
              <div className="templates-field">
                <label>{t.channel === 'email' ? 'Body' : 'SMS text'}</label>
                <textarea
                  rows={t.channel === 'email' ? 8 : 3}
                  value={t.body}
                  onChange={(e) => onChange(t.id, { body: e.target.value })}
                  maxLength={20_000}
                />
                <div className="templates-preview">
                  Preview: <em>{renderPreview(t.body, t.meetingTypeName)}</em>
                </div>
              </div>
              <button
                type="button"
                className="advisor-toggle advisor-toggle-on"
                disabled={saving}
                onClick={() => onSave(t)}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function tierLabelDisplay(label: string): string {
  switch (label) {
    case '3day':  return '3 days before';
    case '1day':  return '1 day before';
    case '1hour': return '1 hour before';
    case '10min': return '10 minutes before';
    default:      return label;
  }
}
